/* eslint-disable max-lines */
/* eslint-disable dot-notation */

import { action, computed, intercept, observable, runInAction } from 'mobx';

import { AnalyticsStore, ContextStore, ExperimentStore, SerializedData, Store } from 'bernie-core';
import { getBexApiContext } from 'bernie-context';
import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';
import { ILocalization, Localization } from 'bernie-l10n';

import { BexApiSource } from 'sources/bex-api-source';

import { adaptPropertyInfoOffers } from 'stores/adapters/bex-api/migration-adapters/property-info/property-info-offers-adapter';
import { getDefaultTimeout, getOffersTimeout } from 'stores/multi-item-store-util';
import { getPropertiesFromResults, getPropertySearchInput } from 'stores/adapters';
import { InfositeQueryAdapter } from 'stores/adapters/infosite-query-adapter';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { STORE_EVENTS } from 'stores/events/store-events';

import {
  DefaultRequestFields,
  HotelMapModel,
  HotelRoomsAndRatesViewModel,
  PropertyLocationScoreInfoModel,
  PropertyRoomScoreInfoModel,
  RateCalendarModel,
} from 'models';
import { MultiItemContext } from 'models/multi-item/multi-item-types';

import {
  ContextInput,
  LodgingPwaPropertySearch,
  OperationResult,
  Points,
  PropertyAvailabilityCheck,
  PropertyFields,
  PropertyLocationScoreInfo,
  PropertyOffers,
  PropertyRoomScoreInfo,
  PropertySearchTravelAds,
  UnitFragment,
} from '__generated__/api';
import { getPropertyAvailabilityCheck, getPropertyOffersInput } from './adapters/bex-api/property-info';
import { MessagingCardProps } from 'components/common/messaging-card/messaging-card';
import { parse, stringify } from 'query-string';
import isPayload, { Payload } from './adobe-payload';

interface CurrentHotelStoreDefaults {
  hotelId?: number;
  selectedRoomType: string;
  selectedRatePlan: string;
  multiItemPriceToken?: string | undefined;
  srpDisplayPrice?: number | undefined;
  srpCurrency?: string | undefined;
  l10n: ILocalization;
  offerSearchData: Record<string, unknown>;
  reviewIdsUpvoted: string[];
  poiData: Record<string, unknown>;
  propertyRoomScoreInfo: PropertyRoomScoreInfoModel[];
  propertyLocationScoreInfo: PropertyLocationScoreInfoModel;
  selectedGalleryCategoryId: string;
  referrer?: string | undefined;
  offerTimestamp?: string | undefined;
}

export class CurrentHotelStore extends Store {
  private SIMILAR_HOTELS_TO_SHOW: number = 10;

  constructor(private bexApiSource: BexApiSource, state: SerializedData = {}, l10n: ILocalization, logger: Logger = NOOP_LOGGER) {
    super(state, logger);

    this.l10n = l10n;

    this.searchCriteria = new SearchCriteriaModel();
    this.numberInterceptor('hotelId');
  }

  public hydrate(data: SerializedData): void {
    if (data && data.hotel && data.hotel.code === 'INACTIVE_HOTEL_ID') {
      window.location.replace(`https://${window.location.host}/Hotels`);
    }

    delete data.bexApiSource;
    delete data.hotelSearchApiSource;

    this.searchCriteria.hydrate(data.searchCriteria || {});
    delete data.searchCriteria;

    Object.assign(this, data);
  }

  // setup mobx interceptor to wrap dates in a method to format data passed to setter
  public numberInterceptor(prop): void {
    if (typeof prop === 'string') {
      intercept(this, prop as any, change => {
        if (change.newValue) {
          const newValue = this.sanitizeNumber(change.newValue);

          if (newValue) {
            change.newValue = newValue;

            return change;
          }
        }
      });
    }
  }

  public sanitizeNumber(number): number | string {
    try {
      const cleanNumber = typeof number === 'number' ? number : parseInt(number, 10);

      if (Number.isNaN(cleanNumber)) {
        throw new Error(`Invalid Number: cannot parse ${number} into a Number`);
      }

      return cleanNumber;
    } catch (error) {
      this.logErrorEvent(STORE_EVENTS.SANATIZE_NUMNBER_ERROR, getErrorString(error));

      return '';
    }
  }

  public static DEFAULTS: CurrentHotelStoreDefaults = {
    hotelId: null,
    selectedRoomType: '',
    selectedRatePlan: '',
    multiItemPriceToken: '',
    srpDisplayPrice: null,
    srpCurrency: '',
    l10n: new Localization('en_US'),
    offerSearchData: {},
    reviewIdsUpvoted: [],
    propertyRoomScoreInfo: [],
    propertyLocationScoreInfo: {},
    poiData: {},
    selectedGalleryCategoryId: 'all',
  };

  @observable
  public l10n: ILocalization;
  @observable
  public toolbar: any;
  @observable
  public property: Partial<PropertyFields.Fragment>;
  @observable
  public map: HotelMapModel;
  @observable
  public offers: HotelRoomsAndRatesViewModel;
  @observable
  public propertyRoomScoreInfo: PropertyRoomScoreInfoModel[] = CurrentHotelStore.DEFAULTS.propertyRoomScoreInfo;
  @observable
  public propertyLocationScoreInfo: PropertyLocationScoreInfoModel = CurrentHotelStore.DEFAULTS.propertyLocationScoreInfo;

  @observable
  public hotelId: number;
  @observable
  public selectedRoomType: string;
  @observable
  public selectedRatePlan: string;
  @observable
  public multiItemPriceToken: string;
  @observable
  public srpDisplayPrice: number;
  @observable
  public srpCurrency: string;
  @observable
  public referrer: string;
  @observable
  public offerTimestamp: string;
  @observable
  public isLoading: boolean;
  @observable
  public isFailure: boolean;
  @observable
  public shouldFetch: boolean;
  @observable
  public shouldFetchOffers: boolean;
  @observable
  public openNewDatePicker: boolean = false;
  @observable
  public loadingOffers: boolean;
  @observable
  public defaultedDates: boolean;
  @observable
  public loadingETPData: boolean;
  @observable
  public creatingConversation: boolean;
  @observable
  public similarHotels: PropertySearchTravelAds.Properties[];

  @observable
  public offerSearchData: any;

  @observable
  public travelAd: any;
  @observable
  public requests: any;
  @observable
  public shouldFetchLocationScore: boolean = false;
  @observable
  public freeLxMessagingCard: MessagingCardProps;

  @observable
  public rateCalendar: RateCalendarModel;
  @observable.ref
  public searchCriteria: SearchCriteriaModel;

  @observable
  public poiData: Record<string, unknown> = CurrentHotelStore.DEFAULTS.poiData;

  private _fetchingHotel: number;

  public selectedMapMarker: number;

  public offerExtensions: any;

  public defaultRequestFields: DefaultRequestFields;

  public apolloLinkSet: boolean = false;

  private detailsPayload: Payload;
  private offersPayload: Payload;

  private resetPayloads() {
    this.detailsPayload = null;
    this.offersPayload = null;
  }

  public addPayload(operation: string, payload: Record<string, unknown>, analytics: AnalyticsStore): void {
    if (isPayload(payload)) {
      if (operation === 'PropertyDetailsQuery') {
        this.detailsPayload = payload;
      }
      if (operation === 'PropertyOffers') {
        this.offersPayload = payload;
      }

      if (this.offersPayload && this.detailsPayload) {
        analytics.publishClientSidePayload([this.detailsPayload, this.formatOmnitureData(this.offersPayload)]);
        this.resetPayloads();
      }
    }
  }

  // Temporary fields to hold the raw bex-api responses to be passed to the Offers shared-ui
  // component. Will be cleaned up once the shared-ui components query the fields themselves.
  @observable
  public sharedUiPropertyReviewAggregatesResponse: PropertyRoomScoreInfo.PropertyReviewAggregates | undefined;
  @observable
  public bexApiPropertyInfoOffersResponse: PropertyOffers.PropertyOffers | undefined;

  @action
  public setPropertyListingAsCurrentHotel(property: PropertyFields.Fragment): void {
    if (!property) {
      return;
    }

    const { id, name, imageGallery, shortList, star, starRatingIcon, vipMessaging } = property;

    const propertyId = parseInt(id, 10);

    this.toolbar = {
      name,
      stars: star,
    };

    if (this.hotelId === propertyId) {
      return;
    }

    this.shouldFetch = true;

    this.property = {
      id,
      name,
      imageGallery,
      shortList,
      star,
      starRatingIcon,
      vipMessaging,
    };

    this.hotelId = propertyId;
  }

  @action
  public updateSearchCriteria(criteria: SearchCriteriaModel): void {
    this.searchCriteria = criteria;
  }

  @action
  public setHotelId(hotelId) {
    const hobPropertyId = this.searchCriteria.expediaPropertyId;
    if (hotelId !== this.hotelId) {
      this.shouldFetch = true;
      this.hotelId = hobPropertyId || hotelId;
    }
  }

  public getRoomScore(roomTypeCode: string): PropertyRoomScoreInfoModel | undefined {
    return this.propertyRoomScoreInfo?.find(x => x.id === roomTypeCode);
  }

  @action
  public resetFlyinMessage(): void {
    if (this.offers && this.offers.flyInModel) {
      this.offers.flyInModel.text = '';
      this.offers.flyInModel.icon = '';
    }
  }

  @computed
  get canDisplayMultipleRatePlansInline(): boolean {
    const propertyUnits = (this.bexApiPropertyInfoOffersResponse?.listings?.filter(
      listing => listing.__typename === 'PropertyUnit'
    ) as unknown) as UnitFragment.Fragment[];
    const hasMultipleAvailableRatePlans = propertyUnits?.filter(propertyUnit => propertyUnit.ratePlans?.length > 1).length > 0;

    return hasMultipleAvailableRatePlans;
  }

  @action
  public fetch(analyticsStore: AnalyticsStore, context: ContextStore, multiItemStore: MultiItemStore, additionalCriteria: any = {}) {
    this.logParallelCalls(this.hotelId); // for logging dups

    this.shouldFetch = false;
    this.isLoading = true;
    this.isFailure = false;

    const timeout = getOffersTimeout(multiItemStore.multiItemContext);

    return this.fetchOffers(context, timeout, analyticsStore, additionalCriteria, multiItemStore)
      .then(
        () =>
          new Promise(resolve => {
            runInAction('updating shared current hotel', () => {
              this.shouldFetch = false;
              this.isLoading = false;
              this.isFailure = false;

              resolve();
            });
          })
      )
      .catch(
        () =>
          new Promise(resolve => {
            runInAction('updating current hotel with failure', () => {
              this.shouldFetch = false;
              this.isFailure = true;
              this.isLoading = false;
              resolve();
            });
          })
      );
  }

  private formatOmnitureData = (omnitureData: Payload) => {
    const firstAttribute = omnitureData.adobeMappedData?.products?.split('=')?.find(Boolean);
    const hotelString = `;Hotel:${this.hotelId};;;`;
    omnitureData.adobeMappedData.products = `${hotelString}${omnitureData.adobeMappedData.products}`;
    if (firstAttribute) {
      omnitureData.adobeMappedData.products = omnitureData.adobeMappedData.products.replace(
        new RegExp(`&${firstAttribute}`, 'g'),
        `,${hotelString}${firstAttribute}`
      );
    }

    return omnitureData;
  };

  private logParallelCalls(hotelId: number) {
    if (this.isLoading === true) {
      // overlapping calls in progress - logging to monitor if issue is widespread
      const message =
        'Overlapping calls in progress. CurrentHotelStore is fetching in parallel, ' +
        `firstHotelId=${this._fetchingHotel} secondHotelId=${hotelId}`;
      this.logger.logEvent(STORE_EVENTS.CURRENT_HOTEL_STORE_PARALLEL_FETCH, message);
    }
    this._fetchingHotel = hotelId;
  }

  private logErrorEvent(name, err) {
    this.logger.logEvent(name, getErrorString(err));
  }

  @action
  public fetchOffers(
    context: ContextStore,
    timeout: number,
    analyticsStore: AnalyticsStore,
    additionalData: any = {},
    multiItemStore?: MultiItemStore
  ): Promise<any> {
    // Note: always fetches offers -- does not check if a fetch is needed
    this.loadingOffers = true;
    this.defaultedDates = false;
    this.shouldFetchOffers = false;

    return this.fetchBexApiSchemaOffers(context, timeout, multiItemStore?.multiItemContext, additionalData)
      .then(response => {
        const start = Date.now();
        const model = adaptPropertyInfoOffers(response);

        runInAction('update current hotel offers from response', () => {
          this.loadingOffers = false;
          this.offers = null;
          this.offerExtensions = response?.extensions;
          this.addPayload('PropertyOffers', this.offerExtensions?.analytics?.[0], analyticsStore);
          if (!this.searchCriteria.useRewards) {
            this.searchCriteria.useRewards = this.getUseRewards(response);
          }

          // Store the raw offers response temporarily for the shared-ui offers migration
          this.bexApiPropertyInfoOffersResponse = {
            __typename: 'OfferDetails',
            alternateDates: response.data.propertyOffers?.alternateDates,
            errorMessage: response.data.propertyOffers?.errorMessage,
            listings: response.data.propertyOffers?.listings,
            categorizedListings: response.data.propertyOffers?.categorizedListings,
            listingsHeader: response.data.propertyOffers?.listingsHeader,
            offerLevelMessages: response.data.propertyOffers?.offerLevelMessages,
            paymentPolicy: response.data.propertyOffers?.paymentPolicy,
            propertyHighlightSection: response.data.propertyOffers?.propertyHighlightSection,
            propertyLevelOffersMessage: response.data.propertyOffers?.propertyLevelOffersMessage,
            shoppingContext: response.data.propertyOffers?.shoppingContext,
            singleUnitOffer: response.data.propertyOffers?.singleUnitOffer,
            singleUnitOfferDialog: response.data.propertyOffers?.singleUnitOfferDialog,
            soldOut: response.data.propertyOffers?.soldOut,
            spaceDetails: response.data.propertyOffers?.spaceDetails,
            stickyBar: response.data.propertyOffers?.stickyBar,
            tripSummary: response.data.propertyOffers?.tripSummary,
            alternateAvailabilityMsg: response.data.propertyOffers?.alternateAvailabilityMsg,
          };

          const bexApiOmnitureAbsent =
            !this.offerExtensions || !this.offerExtensions.analytics || this.offerExtensions.analytics.size === 0;
          if (bexApiOmnitureAbsent && this.logger && typeof this.logger.logEvent === 'function') {
            const error = new Error('Empty omnitureData returned with offers');
            this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
          }

          this.logger.logEvent(STORE_EVENTS.BEX_API_OFFERS_ADAPT_TIME, `${(Date.now() - start).toString()} ms`);
          this.offers = model;
          /* istanbul ignore next */
          if (this.offers.shoppingContext?.multiItem) {
            const { id } = this.offers.shoppingContext.multiItem;
            multiItemStore.updateSessionId(id);
            this.searchCriteria.misId = id;
            this.defaultRequestFields = { misId: id };
          }
        });

        this.defaultedDates = this.getDefaultedDates(response);

        return model;
      })
      .catch(error => {
        runInAction('recovering from hotel offers call breakdown', () => {
          this.loadingOffers = false;
          this.isFailure = true;
          this.logErrorEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
        });

        return { error };
      });
  }

  private getUseRewards(data) {
    const rewardsData = data?.data?.propertyOffers?.searchCriteria?.secondary?.selections?.find(
      selection => selection.value === Points.SHOP_WITH_POINTS
    );

    return rewardsData ? 'true' : 'false';
  }

  private getDefaultedDates(response) {
    return (
      !this.searchCriteria.startDate &&
      !this.searchCriteria.endDate &&
      response.data.propertyOffers?.searchCriteria?.primary.dateRange !== null
    );
  }

  private fetchBexApiSchemaOffers(
    context: ContextStore,
    timeout: number,
    multiItemContext?: MultiItemContext,
    additionalCriteria?: Record<string, unknown>
  ) {
    const variables: PropertyOffers.Variables = getPropertyOffersInput(context, this, multiItemContext, additionalCriteria);

    return this.bexApiSource.fetchPropertyInfoOffersBexFeatures(variables, timeout);
  }

  @action
  public async fetchPropertyAvailabilityCheck(
    context: ContextStore,
    formData: any
  ): Promise<OperationResult<PropertyAvailabilityCheck.Query>> {
    const { hotelId, searchCriteria } = this;
    const criteria = Object.assign({}, searchCriteria, InfositeQueryAdapter.parseIn(formData));
    this.loadingOffers = true;
    const variables: PropertyAvailabilityCheck.Variables = getPropertyAvailabilityCheck(context, criteria, hotelId.toString());

    return new Promise<OperationResult<PropertyAvailabilityCheck.Query>>(async resolve => {
      const data = await this.bexApiSource.fetchPropertyAvailabilityCheck(variables);
      this.loadingOffers = false;
      resolve(data);
    });
  }

  @action
  public setOfferData(offerData): void {
    this.offerSearchData = Object.assign({}, this.offerSearchData, offerData);
  }

  @action
  public clearRoomsFromOffers(): void {
    return this.offers && this.offers.rooms && (this.offers.rooms = null);
  }

  @action
  public fetchPropertyLocationScoreInfo(context: ContextStore, propertyId: number, pageId: string): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);
    const variables: PropertyLocationScoreInfo.Variables = {
      context: contextInput,
      propertyIds: [propertyId.toString()],
    };

    return this.bexApiSource
      .fetchPropertyLocationScoreInfo(variables, pageId, getDefaultTimeout())
      .then(({ data }) => {
        runInAction('update propertyLocationScoreInfo from response', () => {
          if (data?.propertyReviewAggregates?.propertySummaries?.length) {
            this.propertyLocationScoreInfo = data.propertyReviewAggregates.propertySummaries[0].locationScore;
          }
        });
      })
      .catch(error => {
        runInAction('recovering from bex api property info location score call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, variables, getErrorString(error));
        });

        return {
          error,
        };
      });
  }

  @action
  public fetchPropertyRoomScoreInfo(context: ContextStore, propertyId: number, pageId: string): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);
    const variables: PropertyRoomScoreInfo.Variables = {
      context: contextInput,
      propertyIds: [propertyId.toString()],
    };
    this.propertyRoomScoreInfo = null;

    return this.bexApiSource
      .fetchPropertyRoomScoreInfo(variables, pageId, getDefaultTimeout())
      .then(({ data }) => {
        if (data?.propertyReviewAggregates?.propertySummaries?.length) {
          this.propertyRoomScoreInfo = data.propertyReviewAggregates.propertySummaries[0].roomsScore;
          this.sharedUiPropertyReviewAggregatesResponse = data.propertyReviewAggregates;
        } else {
          this.sharedUiPropertyReviewAggregatesResponse = {
            __typename: 'PropertyReviewAggregates',
          };
        }
      })
      .catch(error => {
        runInAction('recovering from bex api property info room score call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, variables, getErrorString(error));
        });

        return {
          error,
        };
      });
  }

  @action
  public getUpdatedBackToHsrUrl(formData) {
    const { startDate, endDate, adults, children } = formData;
    const relativePath = this.offers?.propertySearchLink?.link.uri.relativePath;
    const [hsrPathName, hsrQuery] = relativePath.split('?');
    const updatedQuery = Object.assign({}, parse(hsrQuery), {
      selected: this.hotelId,
      startDate,
      endDate,
      adults,
      children,
    });

    return `${hsrPathName}?${stringify(updatedQuery)}`;
  }

  @computed
  get hsrQueryValues() {
    const relativePath = this.offers?.propertySearchLink?.link.uri.relativePath;
    if (relativePath) {
      const hsrQuery = relativePath.split('?')[1];
      const searchQuery = parse(hsrQuery);
      searchQuery.selected = this.hotelId;

      delete searchQuery.adults;
      delete searchQuery.children;
      delete searchQuery.startDate;
      delete searchQuery.endDate;

      return searchQuery;
    }

    return {};
  }

  /**
   * Fetches sponsored hotels (MeSo) shown on the dateless pdp page.
   * These may or may not be very different from the actual current hotel.
   * @param context
   * @param experiments
   * @param multiItemContext
   */
  @action
  public fetchSimilarHotels(context: ContextStore, multiItemContext: MultiItemContext, pageId: string, experiments?: ExperimentStore) {
    this.similarHotels = null;

    if (!this.hotelId || !this.searchCriteria || !this.searchCriteria.regionId || this.searchCriteria.packageRates === 'true') {
      return Promise.resolve();
    }

    const criteriaWithParsedGuests = SearchQueryAdapter.parseOut(this.searchCriteria && this.searchCriteria.toJSON());

    const similarHotelsCriteria = {
      ...criteriaWithParsedGuests,
      count: this.SIMILAR_HOTELS_TO_SHOW,
      rooms: this.searchCriteria.rooms,
    };

    if (!this.searchCriteria.star) {
      similarHotelsCriteria.star = '10,20,30,40,50';
    }

    const promise: Promise<any> = this.fetchTravelAdPropertySearch(context, similarHotelsCriteria, multiItemContext, pageId, experiments);

    return promise
      .then(response => {
        runInAction('updating dated HIS travelAd carousel results', () => {
          if (response.data) {
            this.similarHotels = getPropertiesFromResults(response.data);
          } else {
            this.similarHotels = response;
          }
        });
      })
      .catch(error => {
        runInAction('dated HIS carousel similar hotels call failure', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
        });
      });
  }

  private fetchTravelAdPropertySearch(
    context: ContextStore,
    criteria: SearchCriteriaModel,
    multiItemContext: MultiItemContext,
    pageId: string,
    experiments?: ExperimentStore
  ) {
    const variables: LodgingPwaPropertySearch.Variables = getPropertySearchInput(
      context,
      criteria,
      null,
      multiItemContext,
      true,
      null,
      experiments
    );
    variables.destination.pinnedPropertyId = this.hotelId.toString();
    if (variables.criteria) {
      variables.criteria.primary.destination.pinnedPropertyId = this.hotelId.toString();
    }

    return this.bexApiSource.fetchTravelAdPropertySearch(variables, pageId);
  }

  @action
  public clearPoiData() {
    this.poiData = CurrentHotelStore.DEFAULTS.poiData;
  }

  @action
  public setOpenNewDatePicker(value: boolean) {
    this.openNewDatePicker = value;
  }

  public setSelectedMapMarker(markerId: number) {
    this.selectedMapMarker = markerId ? Number(markerId) : markerId;
  }
}
